/**
 * modelSummary
 * ------------
 *
 * October 2026
 *
 * The model setup page's config and experiment panel as a compact summary for
 * the AI Assistant's page context, so it can answer about individual elements.
 */

// Caps per list, to keep the context small for very large models.
const MAX_ITEMS = 60;

/** "constant 10", "uniform lower_bound=2, upper_bound=5", ... */
export function describeDist(dist) {
  if (dist === null || dist === undefined) return null;
  if (typeof dist !== "object") return String(dist);
  const attr = dist.attr || {};
  const keys = Object.keys(attr);
  if (dist.dist === "constant" && keys.length === 1) return `constant ${attr[keys[0]]}`;
  const args = keys.map((key) => `${key}=${attr[key]}`).join(", ");
  return args ? `${dist.dist} ${args}` : dist.dist || null;
}

function capped(items, label) {
  if (items.length <= MAX_ITEMS) return { [label]: items };
  return { [label]: items.slice(0, MAX_ITEMS), [`${label}_not_shown`]: items.length - MAX_ITEMS };
}

/**
 * @param {Object} config - The app's configuration state.
 * @returns {Object} Entity types, actions and resources with their settings.
 */
export function summarizeModel(config) {
  const resources = config.resources || {};
  const resourceName = (gid) => resources[gid]?.name || gid;

  const entityTypes = Object.values(config.entity_types || {}).map((entity) => ({
    name: entity.name,
    interarrival: describeDist(entity.interarrival),
    entities_per_arrival: describeDist(entity.entities_per_arrival),
    max_entities: entity.capacity?.is_enabled ? entity.capacity.value : "unlimited",
  }));

  const actions = Object.values(config.actions || {}).map((action) => ({
    name: action.name,
    type: action.type,
    duration: describeDist(action.time),
    units: action.units,
    resources: Object.entries(action.resources || {}).map(([gid, use]) => ({
      resource: resourceName(gid),
      relation: use.rel,
      amount: use.request_amount,
    })),
    ...(action.grouping_enabled ? { grouping_min: describeDist(action.group_min) } : {}),
  }));

  const resourceList = Object.values(resources).map((resource) => ({
    name: resource.name,
    amount: resource.initialAmount,
    queue_discipline: resource.queueDiscipline,
    ...(Object.keys(resource.priorityRanks || {}).length ? { priority_ranks: resource.priorityRanks } : {}),
    has_costs: (resource.costs || []).length > 0,
  }));

  return {
    ...capped(entityTypes, "entity_types"),
    ...capped(actions, "actions"),
    ...capped(resourceList, "resources"),
  };
}

// The values a selected parameter sweeps, for the current experiment type.
function sweepOf(param, experimentType) {
  if (experimentType === "Factorial") return { levels: param.values || [] };
  const sweep = { distribution: param.dist };
  ["constant", "min", "max", "arg3", "arg4"].forEach((key) => {
    if (param[key] !== null && param[key] !== undefined) sweep[key] = param[key];
  });
  return sweep;
}

/**
 * @param {Object} config             - The app's configuration state.
 * @param {Object} setup              - {selectedParameters, numLevels} from the experiment panel.
 * @returns {Object} The experiment being set up.
 */
export function summarizeExperimentSetup(config, setup) {
  const experiment = config.experiment || {};
  const type = experiment.type || "None";
  const summary = { name: experiment.name, type, trials: config.system?.trials };
  if (type === "None") return summary;
  if (type === "Factorial") {
    summary.levels = setup?.numLevels;
    summary.response_variable = experiment.responseVariable || null;
  }
  const params = (setup?.selectedParameters || []).map((param) => ({
    element: param.name,
    element_type: param.type,
    parameter: param.parameter,
    ...sweepOf(param, type),
  }));
  return { ...summary, ...capped(params, "parameters") };
}
